import { useEffect, useEffectEvent, useMemo } from 'react'
import { useMutation, useQuery } from '~/shared/hooks/query'
import keyBy from 'lodash/keyBy'
import { useQueryClient } from '@tanstack/react-query'
import {
  getCaseKey,
  invalidateCachedCaseData,
  listCasesKey,
  listPersonTodoKey,
} from '~/shared/lib/queryKeys'
import { mapPreviewTasksToTasks, type TaskFormDraft } from '~/entities/task'
import {
  providersCases,
  providersCasesHistory,
  providersCasesEmail,
  providersCasesMessageTranscript,
  providersCasesAddRelation,
  providersCasesFromCategoryPreview,
  bffCrmCasesCategories,
  BffCrmCasesCategoriesRetrieveResponse,
  ProvidersCasesListResponse,
  ProvidersCasesRetrieveResponse,
  ProvidersCasesCreateResponse,
  ProvidersCasesPartialUpdateResponse,
  ProvidersCasesCreateBodyParams,
  ProvidersCasesPartialUpdateBodyParams,
  ProvidersCasesAddRelationPartialUpdateBodyParams,
} from '@fireflyhealth/api'
import { required } from '~/shared/lib/required'
// Category as served by the generated BFF client (snake_case). Derived from the
// generated response so it stays in sync with the contract and trims to served fields.
export type CaseCategory = BffCrmCasesCategoriesRetrieveResponse['results'][number]

const listPersonCaseCategoriesKey = 'listPersonCaseCategories'
async function listPersonCaseCategories(personId?: number | null): Promise<CaseCategory[]> {
  // When personId is provided, fetch the staff-filtered categories for that person.
  // When personId is undefined/null, fetch the full list from the backend (no person filter).
  // Omit the `params` key entirely: the generated client validates it whenever present.
  const firstConfig = personId != null ? { params: { person_id: personId } } : {}
  const { data: firstPage } = await bffCrmCasesCategories.retrieve(firstConfig)

  const results: CaseCategory[] = [...(firstPage.results || [])]
  const total = firstPage.count || 0

  let offset = results.length
  const pageSize = 100
  // A page shorter than pageSize is the last page (the backend default limit equals
  // pageSize). This also guards against a `count` that exceeds the real result set so
  // an inconsistent count can never drive an unbounded fetch loop.
  let lastPageSize = results.length
  while (offset < total && lastPageSize >= pageSize) {
    const pageParams =
      personId != null
        ? { person_id: personId, limit: pageSize, offset }
        : { limit: pageSize, offset }
    const { data: page } = await bffCrmCasesCategories.retrieve({ params: pageParams })
    const pageResults = page.results || []
    if (pageResults.length === 0) break
    results.push(...pageResults)
    offset += pageResults.length
    lastPageSize = pageResults.length
  }

  return results
}

// Shared so the empty result keeps one identity across renders while the list loads.
const NO_CASE_CATEGORIES: CaseCategory[] = []

export const usePersonCaseCategories = (personId: number | null) => {
  const isPersonScoped = personId != null

  const { data, isPending: isLoading } = useQuery(
    [listPersonCaseCategoriesKey, personId],
    () => listPersonCaseCategories(personId ?? undefined),
    {
      // Person-scoped lists must not be cached because they depend on person
      // metadata. The full catalog (personId === null) can be cached aggressively.
      staleTime: isPersonScoped ? 0 : Infinity,
      gcTime: isPersonScoped ? 0 : undefined,
    },
    {
      error: 'Failed to fetch case categories',
    },
  )

  return { result: data ?? NO_CASE_CATEGORIES, isLoading }
}

export const usePersonCaseCategoriesById = (personId: number | null) => {
  const { result: categories, isLoading } = usePersonCaseCategories(personId)
  const result = useMemo(() => {
    return keyBy(categories, category => category.id)
  }, [categories])

  return { result, isLoading }
}

async function listCaseEmail(caseId: number) {
  const { data } = await providersCasesEmail.retrieve({ id: caseId, provider_id: 'me' })
  return data
}

export const listCaseEmailKey = 'listCaseEmail'
export const useCaseEmail = (caseId: number) => {
  const { data, isPending: isLoading } = useQuery(
    [listCaseEmailKey, caseId],
    () => listCaseEmail(caseId),
    {
      enabled: caseId !== null,
    },
    {
      error: 'Failed to fetch case email',
    },
  )

  return { result: data, isLoading }
}

// The mappers below default the nullable task fields on read and shape the
// write body (carrying `id` for in-place updates and placing `due_date`), and
// are shared across the CRUD hooks and their consumers.

export type CaseListItem = ProvidersCasesListResponse['results'][number]
type CaseReadResponse =
  | ProvidersCasesRetrieveResponse
  | ProvidersCasesCreateResponse
  | ProvidersCasesPartialUpdateResponse
  | CaseListItem

// Maps the case tasks into the TaskFormDraft shape forms edit, defaulting nullable fields.
export const mapResponseTasksToDrafts = (
  tasks: NonNullable<CaseReadResponse['tasks']>,
): TaskFormDraft[] =>
  tasks.map(task => ({
    id: task.id,
    title: task.title ?? '',
    due_date: task.due_date ?? '',
    owner_group: task.owner_group ?? undefined,
    priority: task.priority ?? 0,
    is_complete: task.is_complete ?? false,
    is_forwarding_message: task.is_forwarding_message ?? undefined,
  }))

// The generated task body omits `id`; in-place task updates must still send it.
type CaseTaskBody = NonNullable<ProvidersCasesCreateBodyParams['tasks']>[number] & { id?: number }

// Builds the generated case task body from an edited TaskFormDraft.
export const mapDraftToTaskBody = (
  task: TaskFormDraft,
  dueDate: string | undefined,
): CaseTaskBody => ({
  ...(task.id !== undefined ? { id: task.id } : {}),
  title: task.title,
  owner_group: task.owner_group,
  priority: task.priority,
  is_complete: task.is_complete,
  ...(task.is_forwarding_message !== undefined
    ? { is_forwarding_message: task.is_forwarding_message }
    : {}),
  ...(dueDate !== undefined ? { due_date: dueDate } : {}),
})

const addPatientCase = async (
  body: ProvidersCasesCreateBodyParams,
): Promise<ProvidersCasesCreateResponse> => {
  const { data } = await providersCases.create({ provider_id: 'me' }, { data: body })
  return data
}

export const useAddCase = () => {
  const client = useQueryClient()

  return useMutation(
    addPatientCase,
    {
      onSuccess: (c: ProvidersCasesCreateResponse) => {
        void client.invalidateQueries({ queryKey: [listCasesKey, c.person] })
        void client.invalidateQueries({ queryKey: [listPersonTodoKey, c.person] })
      },
    },
    { error: 'Failed to add case' },
  )
}

const getCase = async (id: number): Promise<ProvidersCasesRetrieveResponse> => {
  const { data } = await providersCases.retrieve({ id, provider_id: 'me' })
  return data
}

// `onError` is how a caller drops a selection that points at a case it cannot load; the entity
// does not know where that selection is kept.
export const useCase = (id: number | null, onError?: () => void) => {
  const query = useQuery(
    [getCaseKey, id],
    () => getCase(required(id, 'a case id')),
    { enabled: id !== null },
    { error: 'Failed to fetch case' },
  )
  const reportError = useEffectEvent(() => onError?.())
  useEffect(() => {
    if (query.error) reportError()
  }, [query.error])
  return query
}

const getCaseHistory = async (id: number) => {
  const { data } = await providersCasesHistory.list({ provider_id: 'me', id })
  return data
}

export const useCaseHistory = (id: number) => {
  return useQuery(
    ['getCaseHistory', id],
    () => getCaseHistory(id),
    {},
    {
      error: 'Failed to fetch case history',
    },
  )
}

const getCaseMessageTranscript = async (id: number) => {
  const { data } = await providersCasesMessageTranscript.retrieve({ id, provider_id: 'me' })
  return data
}

export const useCaseMessageTranscript = (id: number) => {
  return useQuery(
    ['getCaseMessageTranscript', id],
    () => getCaseMessageTranscript(id),
    {},
    {
      error: 'Failed to fetch case message transcript',
    },
  )
}

/**
 * getCasesForPatient
 * @param personId: person id
 * @returns filtered cases for patient
 */
const getCasesForPatient = async (personId: number): Promise<CaseListItem[]> => {
  const { data } = await providersCases.list(
    { provider_id: 'me' },
    { params: { person: personId } },
  )
  return data.results || []
}

export const useCases = (variables: { personId: number }) => {
  const { personId } = variables
  return useQuery(
    [listCasesKey, personId],
    () => getCasesForPatient(personId),
    {
      retryDelay: 500,
      retry: 3,
    },
    {
      error: 'Failed to fetch cases',
    },
  )
}

const updatePatientCase = async (
  id: number,
  body: ProvidersCasesPartialUpdateBodyParams,
): Promise<ProvidersCasesPartialUpdateResponse> => {
  const { data } = await providersCases.partialUpdate({ id, provider_id: 'me' }, { data: body })
  return data
}

/** The relation to add, in the generated snake_case `new_relation` shape. */
export type CaseRelationInput = NonNullable<
  ProvidersCasesAddRelationPartialUpdateBodyParams['new_relation']
>

export const useAddNewCaseRelation = (personId: number) => {
  const client = useQueryClient()

  return useMutation(
    ({ caseObj, relationData }: { caseObj: { id: number }; relationData: CaseRelationInput }) =>
      providersCasesAddRelation.partialUpdate(
        { id: caseObj.id, provider_id: 'me' },
        { data: { new_relation: relationData } },
      ),
    {
      onSuccess: async () => {
        await invalidateCachedCaseData(client, personId)
      },
    },
    { error: 'Failed to update case' },
  )
}

export const useUpdateCase = () => {
  const client = useQueryClient()

  return useMutation(
    ({ id, body }: { id: number; body: ProvidersCasesPartialUpdateBodyParams }) =>
      updatePatientCase(id, body),
    {
      onSuccess: async (caseObj: ProvidersCasesPartialUpdateResponse) => {
        await invalidateCachedCaseData(client, caseObj.person ?? null)
      },
    },
    { error: 'Failed to update case' },
  )
}

export const useTaskCollectionPreviewMutation = () => {
  return useMutation(
    async (args: {
      caseCategoryId: number
      userId: number
      meAssigneeGroup: number
    }): Promise<{ taskCollectionPk: number | null; taskPreviews: TaskFormDraft[] }> => {
      const { data } = await providersCasesFromCategoryPreview.retrieve(
        { id: args.caseCategoryId, provider_id: 'me' },
        { params: { user_id: args.userId } },
      )
      return {
        taskCollectionPk: data.task_collection_pk,
        taskPreviews: mapPreviewTasksToTasks(data.task_previews, {
          defaultAssigneeGroup: args.meAssigneeGroup,
        }),
      }
    },
    {},
    { error: "Failed to fetch Case Category's tasks" },
  )
}
